import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";

import { pointerFor } from "../../assessments/validate.js";
import type {
  Decorator,
  Languages,
  Order,
  StructuralDecorator,
  StructuralFormView,
  StructuralGroup,
  StructuralGroupView,
  StructuralItemView,
  TextDecorator,
  TextGroup,
  TextGroupView,
  TextItemView,
  TextShown,
} from "./decorator.js";
import {
  bannerPlacements,
  type Banner,
  type FormElement,
  type Shown,
} from "./plainForm.js";
import { refusal, type Refusal } from "./refusal.js";
import {
  groupBeyondTheInstrument,
  itemBeyondTheInstrument,
  textGroupBeyond,
  textItemBeyond,
} from "./withinTheInstrument.js";

/**
 * Every key an order lists more than once, which would leave its place
 * ambiguous: a fault in the order itself, whichever version it is applied to.
 */
const repeatsIn = (order: Order): Refusal[] =>
  (["groups", "items"] as const).flatMap((list) =>
    [...new Set(order[list])]
      .filter(
        (key) => order[list].indexOf(key) !== order[list].lastIndexOf(key),
      )
      .map((key) =>
        refusal({
          message: "An order lists a key more than once",
          issues: [`"${key}" is listed more than once in the order's ${list}`],
        }),
      ),
  );

const doesNotSuit = (at: { group: string; item: string }, why: string) =>
  refusal({
    message: "A decorator does not suit its item",
    issues: [`${pointerFor([at.group, at.item])}: ${why}`],
  });

const doesNotSuitGroup = (at: { group: string }, why: string) =>
  refusal({
    message: "A decorator does not suit its group",
    issues: [`${pointerFor([at.group])}: ${why}`],
  });

type GroupElement = Extract<FormElement, { kind: "group" }>;

type ItemElement = Extract<FormElement, { kind: "item" }>;

/** An item as a structural decorator sees it. */
const structuralViewOf = ({
  at,
  responseSet,
  presentation: { control, acceptsComment },
  asking,
  checks,
}: ItemElement): StructuralItemView => ({
  at,
  ...(responseSet === undefined ? {} : { responseSet }),
  control:
    control.kind === "choice"
      ? { kind: "choice", values: control.options.map(({ value }) => value) }
      : control,
  required: asking.required,
  asked: asking.when,
  checks,
  acceptsComment,
  commentRequired: asking.commentRequired,
});

/** A group as a structural decorator decides it. */
const structuralGroupOf = ({ at, asking }: GroupElement): StructuralGroup => ({
  at,
  required: asking.required,
  asked: asking.when,
});

const withGroup = (
  element: GroupElement,
  group: StructuralGroup,
): GroupElement => ({
  ...element,
  asking: { when: group.asked, required: group.required },
});

/**
 * An item with a decorator's view written into it. A choice keeps its
 * options' words, which a view does not carry: `itemBeyondTheInstrument` passes
 * only the instrument's own values, in order, so the options are unchanged.
 * Whether it takes a comment, and the response set it draws on, are kept
 * from the element too, since `itemBeyondTheInstrument` refuses any change
 * to either.
 */
const withView = (
  element: ItemElement,
  view: StructuralItemView,
): ItemElement => ({
  ...element,
  presentation: {
    ...element.presentation,
    control:
      view.control.kind === "choice"
        ? element.presentation.control
        : view.control,
  },
  asking: {
    when: view.asked,
    required: view.required,
    commentRequired: view.commentRequired,
  },
  checks: view.checks,
});

const shownOf = ({ title, description }: Shown): TextShown => ({
  title,
  ...(description === undefined ? {} : { description }),
});

/** An item as a text decorator sees it. */
const textViewOf = (
  element: ItemElement,
  languages: Languages,
): TextItemView => {
  const { control, banners } = element.presentation;

  return {
    ...structuralViewOf(element),
    ...languages,
    ...shownOf(element.presentation),
    answerOptions: control.kind === "choice" ? control.options : [],
    banners,
  };
};

/**
 * An item with a text decorator's view written into it: its words and
 * banners, since `textItemBeyond` refuses any change to what the view may
 * only read.
 */
const withTextView = (
  element: ItemElement,
  { banners, answerOptions, ...view }: TextItemView,
): ItemElement => {
  const { control, acceptsComment } = element.presentation;

  return {
    ...element,
    presentation: {
      ...shownOf(view),
      control:
        control.kind === "choice"
          ? { ...control, options: answerOptions }
          : control,
      acceptsComment,
      // `textItemBeyond` refuses a banner without words, so the filter drops
      // none; sorted stably, so banners sharing a placement keep their order.
      banners: banners
        .filter((banner): banner is Banner => banner.text !== undefined)
        .toSorted(
          (left, right) =>
            bannerPlacements.indexOf(left.placement) -
            bannerPlacements.indexOf(right.placement),
        ),
    },
  };
};

/** A group as a text decorator decides it. */
const textGroupOf = (
  element: GroupElement,
  languages: Languages,
): TextGroup => ({
  ...structuralGroupOf(element),
  ...languages,
  ...shownOf(element.presentation),
});

const withTextGroup = (
  element: GroupElement,
  group: TextGroup,
): GroupElement => ({
  ...element,
  presentation: shownOf(group),
});

/** An element being decorated, beside its plain form, which every decorator's result is judged against. */
type InProgress =
  | {
      readonly kind: "group";
      readonly element: GroupElement;
      readonly plain: GroupElement;
    }
  | {
      readonly kind: "item";
      readonly element: ItemElement;
      readonly plain: ItemElement;
    };

/**
 * An entry with a visit's result written into it, or left as it was where the
 * result was refused, each refusal added to `refused`: a refused result is
 * never written back, so no later decorator sees it and each fault is
 * reported once, where it was made.
 */
const judged = <Entry extends InProgress>(
  entry: Entry,
  beyond: readonly string[],
  refusalFor: (why: string) => Refusal,
  written: () => Entry["element"],
  refused: Refusal[],
): Entry => {
  refused.push(...beyond.map(refusalFor));

  return beyond.length === 0 ? { ...entry, element: written() } : entry;
};

/**
 * The form after one structural decorator's visits, every one seeing the form
 * as it stood before them, with each refused result (one beyond the
 * instrument, or an order that cannot be applied) added to `refused`.
 */
const afterStructuralVisits = (
  current: readonly InProgress[],
  { group: visitGroup, item: visitItem, form: visitForm }: StructuralDecorator,
  refused: Refusal[],
): InProgress[] => {
  const viewOfGroup = (element: GroupElement): StructuralGroupView => ({
    ...structuralGroupOf(element),
    items: current.flatMap((entry) =>
      entry.kind === "item" && entry.element.at.group === element.at.group
        ? [structuralViewOf(entry.element)]
        : [],
    ),
  });

  const visited = current.map((entry): InProgress => {
    if (entry.kind === "group") {
      if (visitGroup === undefined) {
        return entry;
      }

      const decorated = visitGroup(viewOfGroup(entry.element));

      return judged(
        entry,
        groupBeyondTheInstrument({
          plain: structuralGroupOf(entry.plain),
          given: structuralGroupOf(entry.element),
          decorated,
        }),
        (why) => doesNotSuitGroup(entry.element.at, why),
        () => withGroup(entry.element, decorated),
        refused,
      );
    }

    if (visitItem === undefined) {
      return entry;
    }

    const decorated = visitItem(structuralViewOf(entry.element));

    return judged(
      entry,
      itemBeyondTheInstrument({
        plain: structuralViewOf(entry.plain),
        given: structuralViewOf(entry.element),
        decorated,
      }),
      (why) => doesNotSuit(entry.element.at, why),
      () => withView(entry.element, decorated),
      refused,
    );
  });

  if (visitForm === undefined) {
    return visited;
  }

  const formView: StructuralFormView = {
    groups: current.flatMap((entry) =>
      entry.kind === "group" ? [viewOfGroup(entry.element)] : [],
    ),
  };
  const order = visitForm(formView);
  const faults = [
    ...repeatsIn(order),
    ...unheadedItemsIn(current.map(({ element }) => element)),
  ];

  refused.push(...faults);

  return faults.length === 0 ? inOrder(visited, order) : visited;
};

/** As `afterStructuralVisits`, for a text decorator, which has no form visit. */
const afterTextVisits = (
  current: readonly InProgress[],
  { group: visitGroup, item: visitItem }: TextDecorator,
  languages: Languages,
  refused: Refusal[],
): InProgress[] => {
  const viewOfGroup = (element: GroupElement): TextGroupView => ({
    ...textGroupOf(element, languages),
    items: current.flatMap((entry) =>
      entry.kind === "item" && entry.element.at.group === element.at.group
        ? [textViewOf(entry.element, languages)]
        : [],
    ),
  });

  return current.map((entry): InProgress => {
    if (entry.kind === "group") {
      if (visitGroup === undefined) {
        return entry;
      }

      const decorated = visitGroup(viewOfGroup(entry.element));

      return judged(
        entry,
        textGroupBeyond({
          plain: textGroupOf(entry.plain, languages),
          given: textGroupOf(entry.element, languages),
          decorated,
        }),
        (why) => doesNotSuitGroup(entry.element.at, why),
        () => withTextGroup(entry.element, decorated),
        refused,
      );
    }

    if (visitItem === undefined) {
      return entry;
    }

    const decorated = visitItem(textViewOf(entry.element, languages));

    return judged(
      entry,
      textItemBeyond({
        plain: textViewOf(entry.plain, languages),
        given: textViewOf(entry.element, languages),
        decorated,
      }),
      (why) => doesNotSuit(entry.element.at, why),
      () => withTextView(entry.element, decorated),
      refused,
    );
  });
};

/**
 * The form with each decorator applied in turn, every one seeing the form as
 * those before it left it; the form after each; and the results refused on
 * the way.
 *
 * A later decorator may change what an earlier one set: decorators are one
 * list, in an order its author chose.
 */
const withDecorators = (
  form: readonly FormElement[],
  decorators: readonly Decorator[],
  languages: Languages,
): { form: FormElement[]; steps: FormElement[][]; refused: Refusal[] } => {
  const refused: Refusal[] = [];
  const steps: (readonly InProgress[])[] = [];
  const decorated = decorators.reduce<readonly InProgress[]>(
    (current, decorator) => {
      const next =
        decorator.kind === "structural"
          ? afterStructuralVisits(current, decorator, refused)
          : afterTextVisits(current, decorator, languages, refused);

      steps.push(next);

      return next;
    },
    form.map((element): InProgress =>
      element.kind === "item"
        ? { kind: "item", element, plain: element }
        : { kind: "group", element, plain: element },
    ),
  );
  const elementsOf = (entries: readonly InProgress[]) =>
    entries.map(({ element }) => element);

  return {
    form: elementsOf(decorated),
    steps: steps.map(elementsOf),
    refused,
  };
};

/** Where a key falls in a list: by its rank, or after every key listed. */
const rankIn = (list: readonly string[], key: string): number => {
  const rank = list.indexOf(key);

  return rank === -1 ? list.length : rank;
};

/**
 * Every item whose group has no heading in the form, which an order could not
 * place: it sorts each group's items under that group's heading.
 */
const unheadedItemsIn = (form: readonly FormElement[]): Refusal[] => {
  const headed = new Set(
    form.flatMap((element) =>
      element.kind === "group" ? [element.at.group] : [],
    ),
  );

  return form
    .filter(
      (element): element is ItemElement =>
        element.kind === "item" && !headed.has(element.at.group),
    )
    .map((element) =>
      refusal({
        message: "An item has no group heading to be ordered under",
        issues: [
          `${pointerFor([element.at.group, element.at.item])}: its group has no heading in the form`,
        ],
      }),
    );
};

/**
 * The form in the sequence an order sets, as `Order` describes.
 *
 * Drops any item whose group has no heading in the form, so it must only be
 * handed a form `unheadedItemsIn` finds nothing wrong with.
 */
const inOrder = (form: readonly InProgress[], order: Order): InProgress[] => {
  const sections = form.flatMap((heading) =>
    heading.kind === "group"
      ? [
          {
            heading,
            items: form.filter(
              (candidate): candidate is Extract<InProgress, { kind: "item" }> =>
                candidate.kind === "item" &&
                candidate.element.at.group === heading.element.at.group,
            ),
          },
        ]
      : [],
  );
  // A stable sort, which is what keeps unlisted keys in the order they had.
  return sections
    .toSorted(
      (left, right) =>
        rankIn(order.groups, left.heading.element.at.group) -
        rankIn(order.groups, right.heading.element.at.group),
    )
    .flatMap(({ heading, items }) => [
      heading,
      ...items.toSorted(
        (left, right) =>
          rankIn(order.items, left.element.at.item) -
          rankIn(order.items, right.element.at.item),
      ),
    ]);
};

type DecorateInput = {
  form: readonly FormElement[];
  decorators: readonly Decorator[];
  /** The language `form` was built in, which text decorators choose words by. */
  language: string;
  /** The language a banner is shown in where it gives nothing in `language`. */
  fallbackLanguage: string;
};

/** A decorated form, and the form as each of its decorators left it, in their order. */
export type DecoratedStepwise = {
  readonly form: FormElement[];
  readonly steps: readonly (readonly FormElement[])[];
};

/**
 * As `decorate`, with the form as each decorator left it, so a report can
 * tell what each one did.
 */
export const decorateStepwise = ({
  form,
  decorators,
  language,
  fallbackLanguage,
}: DecorateInput): Result<DecoratedStepwise, Refusal> => {
  const {
    form: decorated,
    steps,
    refused,
  } = withDecorators(form, decorators, {
    language,
    fallbackLanguage,
  });

  return refused.length === 0
    ? ok({ form: decorated, steps })
    : err(
        refusal({
          message: "The decorators do not apply",
          issues: refused.flatMap((problem) => problem.issues),
          suppressed: refused,
        }),
      );
};

/**
 * A form with its decorators applied, in order.
 *
 * Refused where a decorator's result goes beyond the instrument, changes what
 * its kind may only read, sets an order that cannot be applied, or leaves a
 * banner without words or by a comment box the item does not take,
 * reporting every problem rather than the first.
 */
export const decorate = (
  input: DecorateInput,
): Result<FormElement[], Refusal> =>
  decorateStepwise(input).map(({ form }) => form);
